import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Radio, Shield, Zap, Activity, CheckCircle2 } from 'lucide-react';

const SolutionSection = () => {
  const features = [
    {
      icon: Radio,
      title: 'Diversidade de meio físico',
      description: 'Fibra Óptica + Rádio Digital = Caminhos totalmente independentes.',
      color: 'text-blue-500'
    },
    {
      icon: Shield,
      title: 'Proteção Total',
      description: 'Comunicação Protegida contra todas as possíveis falhas.',
      color: 'text-green-500'
    },
    {
      icon: Zap,
      title: 'Failover Automático',
      description: 'Comutação automática e imperceptível para sua operação',
      color: 'text-yellow-500'
    },
    {
      icon: Activity,
      title: 'Gerenciamento & Suporte',
      description: 'Gerenciamento proativo da solução e suporte técnico especializado próprio',
      color: 'text-purple-500'
    }
  ];

  const specs = [
    { value: '99,99%', label: 'Disponibilidade', sublabel: 'SLA garantido em contrato', icon: Activity },
    { value: '< 30ms', label: 'Failover', sublabel: 'Comutação automática', icon: Zap },
    { value: '100%', label: 'Banda Garantida', sublabel: 'Velocidade simétrica', icon: CheckCircle2 },
    { value: '24/7', label: 'Suporte', sublabel: 'COR próprio TMC', icon: Activity }
  ];

  return (
    <section id="solucao" className="py-16 md:py-24 bg-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzAwNzliZSIgc3Ryb2tlLXdpZHRoPSIwLjUiIG9wYWNpdHk9IjAuMDUiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-40"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
          <span className="text-sm font-semibold tracking-wider uppercase text-[#0079be] mb-2 block">
            SOLUÇÃO TMC
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Conectividade Blindada com Redundância Física Real
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            A TMC entrega a verdadeira redundância para a sua empresa combinando Fibra Óptica com Rádio Digital. São dois caminhos 100% independentes trabalhando juntos para garantir a continuidade dos seus negócios, aconteça o que acontecer na rua.
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 md:p-12 shadow-2xl">
          {/* Left Column - Image */}
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31"
              alt="Network Redundancy Infrastructure"
              className="w-full h-80 object-cover rounded-xl opacity-80"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white space-y-4 bg-black/30 backdrop-blur-sm p-8 rounded-2xl">
                <h3 className="text-2xl md:text-3xl font-bold">NETWORK REDUNDANCY</h3>
                <p className="text-lg">HIGH AVAILABILITY - DIVERSE PATHS</p>
              </div>
            </div>
          </div>

          {/* Right Column - Features */}
          <div className="text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-8">
              Como Funciona a Solução TMC
            </h3>
            
            <div className="space-y-6 mb-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="flex items-start space-x-4 p-4 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-colors duration-300 border border-white/20"
                  >
                    <div className="flex-shrink-0">
                      <div className={`w-12 h-12 rounded-lg bg-white shadow-md flex items-center justify-center`}>
                        <Icon className={`w-6 h-6 ${feature.color}`} />
                      </div>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold mb-1">{feature.title}</h4>
                      <p className="text-gray-300">{feature.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <Button
              size="lg"
              className="w-full md:w-auto bg-white hover:bg-gray-100 text-gray-900 font-semibold px-8 py-6 text-lg transition-all duration-300 hover:shadow-xl border-4 border-yellow-400 hover:border-yellow-300"
              onClick={() => document.getElementById('contato')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Solicitar Análise Técnica
            </Button>
          </div>
        </div>

        {/* Technical Specs - Destaque Azul */}
        <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
          <h3 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-8">
            Especificações Técnicas
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {specs.map((spec, index) => {
              const Icon = spec.icon;
              return (
                <Card key={index} className="text-center hover:shadow-2xl transition-all duration-300 border-2 border-[#0079be]/20 hover:border-[#0079be] bg-gradient-to-br from-blue-50 to-white">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#0079be] flex items-center justify-center shadow-lg">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-5xl font-black text-[#0079be] mb-3 drop-shadow-sm">{spec.value}</div>
                    <div className="text-xl font-bold text-gray-900 mb-2">{spec.label}</div>
                    <div className="text-sm text-gray-600 font-medium">{spec.sublabel}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;
